import React, { useState, useEffect, useRef } from 'react';
import { X, Printer, Download, ZoomIn, ZoomOut, RotateCcw, AlertCircle } from 'lucide-react';

function getFileType(mime, nombre) {
  const m = mime || '';
  const ext = (nombre || '').split('.').pop().toLowerCase();
  if (m.includes('pdf') || ext === 'pdf') return 'pdf';
  if (m.includes('image') || ['jpg','jpeg','png','gif','bmp','webp'].includes(ext)) return 'image';
  if (m.includes('word') || ['doc','docx'].includes(ext)) return 'word';
  if (m.includes('sheet') || m.includes('excel') || ['xls','xlsx','csv'].includes(ext)) return 'excel';
  return 'unknown';
}

export function DocumentViewer({ archivo, onClose }) {
  const [zoom, setZoom] = useState(100);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const tipo = getFileType(archivo.tipo_mime, archivo.nombre_original);
  const iframeRef = useRef();

  // Cerrar con Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // Cargar archivo con fetch autenticado y convertir a blob URL
  useEffect(() => {
    const token = localStorage.getItem('token');
    const backendUrl = window.location.port === '3000'
      ? `http://localhost:3001/api/archivos/${archivo.id}/ver`
      : `/api/archivos/${archivo.id}/ver`;

    setLoading(true);
    setError('');

    fetch(backendUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'No se pudo cargar el archivo');
        setLoading(false);
      });

    // Limpiar blob URL al cerrar
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [archivo.id]);

  const handlePrint = () => {
    if (!blobUrl) return;
    if (tipo === 'pdf' && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
        return;
      } catch(e) {}
    }
    if (tipo === 'image') {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(`<!DOCTYPE html><html><head><title>${archivo.nombre_original}</title>
          <style>body{margin:0;background:#fff;text-align:center}img{max-width:100%}</style></head>
          <body><img src="${blobUrl}" onload="setTimeout(()=>window.print(),300)"/></body></html>`);
        win.document.close();
      }
      return;
    }
    window.open(blobUrl, '_blank');
  };

  const handleDescargar = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = archivo.nombre_original;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const btnS = {
    background: 'rgba(255,255,255,.12)', border: 'none', color: 'white',
    borderRadius: 7, padding: '7px 10px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: '0.82rem', fontWeight: 500, fontFamily: 'inherit'
  };

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:9999, display:'flex', flexDirection:'column' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* ── Barra superior ── */}
      <div style={{ background:'#1a1a1a', borderBottom:'1px solid rgba(255,255,255,.1)', padding:'10px 16px', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:'white', fontWeight:600, fontSize:'0.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {archivo.nombre_original}
          </div>
          <div style={{ color:'rgba(255,255,255,.4)', fontSize:'0.72rem', marginTop:1 }}>
            {tipo.toUpperCase()} {archivo.tamaño ? `· ${(archivo.tamaño/1024).toFixed(0)} KB` : ''}
          </div>
        </div>

        {tipo === 'image' && (
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <button onClick={() => setZoom(z => Math.max(25,z-25))} style={btnS}><ZoomOut size={15}/></button>
            <span style={{ color:'rgba(255,255,255,.6)', fontSize:'0.8rem', minWidth:40, textAlign:'center' }}>{zoom}%</span>
            <button onClick={() => setZoom(z => Math.min(300,z+25))} style={btnS}><ZoomIn size={15}/></button>
            <button onClick={() => setZoom(100)} style={btnS}><RotateCcw size={13}/></button>
          </div>
        )}

        <button onClick={handlePrint} style={btnS} disabled={!blobUrl}>
          <Printer size={15}/> Imprimir
        </button>

        <button onClick={handleDescargar} style={{ ...btnS, background:'var(--color-primary)' }} disabled={!blobUrl}>
          <Download size={15}/> Descargar
        </button>

        <button onClick={onClose} style={{ ...btnS, padding:'7px 9px' }} title="Cerrar (Esc)">
          <X size={17}/>
        </button>
      </div>

      {/* ── Contenido ── */}
      <div style={{ flex:1, overflow:'auto', display:'flex', alignItems: tipo==='image'?'flex-start':'stretch', justifyContent:'center', padding: tipo==='image'?24:0, background:'#2a2a2a' }}>

        {/* Cargando */}
        {loading && (
          <div style={{ alignSelf:'center', textAlign:'center', color:'white' }}>
            <div className="spinner" style={{ width:40, height:40, margin:'0 auto 16px', borderColor:'rgba(255,255,255,.2)', borderTopColor:'var(--color-primary)' }}/>
            <div style={{ opacity:.6 }}>Cargando documento...</div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{ alignSelf:'center', textAlign:'center', color:'white', padding:40 }}>
            <AlertCircle size={48} style={{ opacity:.3, marginBottom:16, display:'block', margin:'0 auto 16px' }}/>
            <h3 style={{ marginBottom:8, opacity:.7 }}>No se pudo cargar el archivo</h3>
            <p style={{ opacity:.45, fontSize:'0.875rem', marginBottom:24 }}>{error}</p>
          </div>
        )}

        {/* PDF */}
        {!loading && !error && blobUrl && tipo === 'pdf' && (
          <iframe
            ref={iframeRef}
            src={blobUrl}
            title={archivo.nombre_original}
            style={{ width:'100%', height:'100%', border:'none', background:'white' }}
          />
        )}

        {/* Imagen */}
        {!loading && !error && blobUrl && tipo === 'image' && (
          <img
            src={blobUrl}
            alt={archivo.nombre_original}
            style={{ maxWidth:`${zoom}%`, height:'auto', borderRadius:4, boxShadow:'0 4px 20px rgba(0,0,0,.5)' }}
          />
        )}

        {/* Word / Excel / Otro */}
        {!loading && !error && blobUrl && (tipo === 'word' || tipo === 'excel' || tipo === 'unknown') && (
          <div style={{ alignSelf:'center', textAlign:'center', color:'white', padding:60 }}>
            <div style={{ fontSize:'4rem', marginBottom:16 }}>
              {tipo==='word'?'📝':tipo==='excel'?'📊':'📄'}
            </div>
            <h3 style={{ marginBottom:8, opacity:.8 }}>{archivo.nombre_original}</h3>
            <p style={{ opacity:.45, marginBottom:28, fontSize:'0.875rem', lineHeight:1.6 }}>
              Este formato no se puede previsualizar en el navegador.<br/>
              Pulsa Descargar para abrirlo con la aplicación correspondiente.
            </p>
            <button onClick={handleDescargar} style={{ background:'var(--color-primary)', color:'white', padding:'12px 24px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:'1rem', display:'inline-flex', alignItems:'center', gap:8, fontFamily:'inherit' }}>
              <Download size={18}/> Descargar archivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentViewer;
